import { PROCEDURE_FIELD_TYPE, PROCEDURE_NODE_TYPE } from "~/constants";

export const getEffectiveHeaderFooter = (procedure, defaultOem) => {
  const headerUrl =
    procedure?.pageHeader?.url ??
    defaultOem?.defaultProcedureHeader?.url ??
    null;
  const footerUrl =
    procedure?.pageFooter?.url ??
    defaultOem?.defaultProcedureFooter?.url ??
    null;
  return { headerUrl, footerUrl };
};

export const cloneAttachments = (attachments, keepId = false) => {
  if (!attachments || !Array.isArray(attachments)) return [];

  const list = [];

  for (const file of attachments) {
    let clonedFile = file;

    // Create a new object without _id if needed
    if (file?._id && !keepId) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _id, ...rest } = file;
      clonedFile = rest;
    }

    if (clonedFile?.url || clonedFile?.signatureUrl) {
      list.push(clonedFile);
      continue;
    }

    const fileObj = new File([clonedFile], clonedFile.name, {
      type: clonedFile.type,
    });
    list.push(fileObj);
  }

  return list;
};

const objectDeepClone = (obj) => {
  const clonedObj = Object.create(Object.getPrototypeOf(obj));

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (value === null || value === undefined) {
        clonedObj[key] = value;
        continue;
      }

      if (typeof value === "object") {
        if (key === "attachments") {
          clonedObj[key] = cloneAttachments(value);
        } else if (Array.isArray(value)) {
          if (value.some((v) => v instanceof File)) {
            clonedObj[key] = cloneAttachments(value);
          } else {
            clonedObj[key] = arrayOfObjectDeepClone(value);
          }
        } else {
          clonedObj[key] = objectDeepClone(value);
        }
      } else {
        clonedObj[key] = value;
      }
    }
  }

  return clonedObj;
};

const arrayOfObjectDeepClone = (arr) => {
  const clonedArr = [];

  for (let i = 0; i < arr.length; i++) {
    const obj = arr[i];
    if (typeof obj !== "object") {
      clonedArr.push(obj);
    } else {
      const clonedObj = objectDeepClone(obj);

      clonedArr.push(clonedObj);
    }
  }

  return clonedArr;
};

export const cloneProcedureTemplate = (template) => {
  const children = arrayOfObjectDeepClone(template.children);
  const clonedObj = JSON.parse(JSON.stringify(template));

  if (Object.prototype.hasOwnProperty.call(template, "signatures")) {
    const hasFileObj = template.signatures.some(
      (signature) => !!signature.signatureFile,
    );

    const signatures = hasFileObj
      ? cloneAttachments(template.signatures, true)
      : template.signatures;
    clonedObj.signatures = signatures;
  }

  if (Object.prototype.hasOwnProperty.call(template, "pageHeader")) {
    const pageHeader = template.pageHeader
      ? cloneAttachments([template.pageHeader])[0]
      : template.pageHeader;
    clonedObj.pageHeader = pageHeader;
  }

  return {
    ...clonedObj,
    children,
  };
};

/**
 * Deep clones a single node (and its children recursively).
 * Used for duplicate operations where File objects in attachments
 * need proper cloning. Much cheaper than cloning the entire tree.
 */
export const cloneNode = (node) => {
  const cloned = { ...node };

  if (node.attachments) {
    cloned.attachments = cloneAttachments(node.attachments);
  }

  if (node.options && Array.isArray(node.options)) {
    cloned.options = node.options.map((opt) => ({ ...opt }));
  }

  if (node.tableOption) {
    cloned.tableOption = {
      ...node.tableOption,
      columns: node.tableOption.columns
        ? node.tableOption.columns.map((col) => ({ ...col }))
        : [],
    };
  }

  if (node.value && Array.isArray(node.value)) {
    cloned.value = node.value.map((row) => ({ ...row }));
  }

  if (node.children && Array.isArray(node.children)) {
    cloned.children = node.children.map((child) => cloneNode(child));
  }

  return cloned;
};

export const handlePaste = (event, value, index, options) => {
  event.preventDefault();
  const pastedText = event.clipboardData.getData("text/plain");

  // Split pasted text into lines
  const lines = pastedText.split(/\r\n|\r|\n/);
  const newOptions = lines
    .filter((line, i) => i !== 0 && !!line)
    .map((line) => ({ name: line }));

  // Get the current input field
  const input = event.target;
  // Get the selection start and end positions
  const selectionStart = input.selectionStart;
  const selectionEnd = input.selectionEnd;

  // Replace the selected text with the pasted text
  const newValue =
    value.slice(0, selectionStart) + lines[0] + value.slice(selectionEnd);

  const updatedOptions = [...options];
  updatedOptions[index].name = newValue;
  updatedOptions.splice(index + 1, 0, ...newOptions);
  return [...updatedOptions];
};

export const removeInvalidValues = async (children) => {
  if (children === null || children === undefined) return children;

  // Create a new array to avoid mutating the original
  const result = [];

  for (const child of children) {
    // Create a shallow copy of the child object
    const newChild = { ...child };

    if (
      newChild.type === PROCEDURE_FIELD_TYPE.IMAGE_UPLOADER_FIELD &&
      newChild.value
    ) {
      newChild.value = newChild.value.filter(
        (child) => child.url && child.type,
      );
    } else if (
      newChild.type === PROCEDURE_FIELD_TYPE.DATE_FIELD &&
      typeof newChild.value !== typeof ""
    ) {
      newChild.value = null;
    } else if (
      newChild.type === PROCEDURE_FIELD_TYPE.NUMBER_FIELD &&
      typeof newChild.value !== typeof "" &&
      typeof newChild.value !== typeof 0
    ) {
      newChild.value = null;
    } else if (newChild.type === PROCEDURE_NODE_TYPE.SECTION) {
      newChild.children = await removeInvalidValues(newChild.children);
    } else if (newChild.value && newChild.value === "") {
      newChild.value = null;
    }

    result.push(newChild);
  }

  return result;
};
